import { Core, Decorative } from "@faire/design-tokens";
// eslint-disable-next-line import/no-unassigned-import
import "@faire/retailer-visitor-shared/components/slick.css";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { BrandsLandingPageTestimonialTile } from "@faire/retailer-visitor-shared/views/brands/landing-page/BrandsLandingPageTestimonialTile";
import {
  getCategoriesGroup,
  ICategoryGroup,
} from "@faire/retailer-visitor-shared/views/brands/landing-page/const";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { trackBrandLeadsBrandsPageCategoryTestimonialClick } from "@faire/web-api--source/events/brandLeads/click/brandsPageCategoryTestimonial";
import { trackBrandLeadsBrandPageCategoryTestimonialView } from "@faire/web-api--source/events/brandLeads/view/brandPageCategoryTestimonial";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";

interface IProps {
  initialSlide?: number;
  isPersonalized?: boolean;
}

const BrandsLandingPageTestimonialsBase: React.FC<IProps> = ({
  initialSlide,
  isPersonalized,
}) => {
  const { isTabletAndAbove } = useViewport();
  const { strictLocalize } = useStrictLocalization();
  const { isEurope: isShowingCaseStudies } = useNationality();

  const [currentIndex, setCurrentIndex] = useState<number>(initialSlide ?? 0);

  useEffect(() => {
    trackBrandLeadsBrandPageCategoryTestimonialView();
  }, []);

  const isSSR = useIsSSR();

  const currentSelectedCategory = getCategoriesGroup(isSSR, strictLocalize)[
    currentIndex
  ];

  if (!currentSelectedCategory) {
    return null;
  }

  return (
    <WhenVisibilityIsImminent>
      <Container
        $bgColor={
          isPersonalized
            ? currentSelectedCategory.backgroundColor
            : Core.surface.inverse
        }
        $opacity={currentSelectedCategory.opacity}
        $showCaseStudies={isShowingCaseStudies}
      >
        {isTabletAndAbove ? (
          <>
            <Spacer size="5x" />
            <StaticCategories
              currentIndex={currentIndex}
              isPersonalized={isPersonalized}
              setCurrentIndex={setCurrentIndex}
              currentSelectedCategory={currentSelectedCategory}
            />
          </>
        ) : null}
      </Container>
    </WhenVisibilityIsImminent>
  );
};

const StaticCategoriesBase = ({
  currentIndex,
  setCurrentIndex,
  currentSelectedCategory,
  isPersonalized,
}: {
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  currentSelectedCategory: ICategoryGroup;
  isPersonalized: boolean | undefined;
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { isMobile, isDesktopAndAbove, isTablet } = useViewport();

  const imageWidth = useResponsiveValue({
    mobileAndAbove: 312,
    tabletAndAbove: 720,
    desktopAndAbove: 820,
    xLargeAndAbove: 915,
  });

  const handleClickRoundedTitle = (index: number, eventKey: string) => {
    setCurrentIndex(index);
    trackBrandLeadsBrandsPageCategoryTestimonialClick(eventKey);
  };

  const isSSR = useIsSSR();

  return (
    <Column align="center">
      <Typography
        variant={
          isDesktopAndAbove
            ? "displayLSerifRegular"
            : isTablet
              ? "displayMSerifRegular"
              : "displaySSerifRegular"
        }
        align="center"
      >
        <StrictLocalMsg defaultMessage="Grow, no matter what you sell" />
      </Typography>
      <Spacer size="1x" />
      <RoundedTitleWrapper>
        {getCategoriesGroup(isSSR, strictLocalize).map((item, index) => (
          <div
            key={index}
            onClick={() => handleClickRoundedTitle(index, item.eventKey)}
          >
            <RoundedTitle $isSelected={index === currentIndex}>
              {item.header}
            </RoundedTitle>
          </div>
        ))}
      </RoundedTitleWrapper>
      <Spacer size="2x" />
      <Column align="center">
        <BrandsLandingPageTestimonialTile
          category={currentSelectedCategory}
          isMobile={isMobile}
          isPersonalized={isPersonalized}
        />
        {isMobile ? (
          <>
            <Spacer size="3x" />
            <LogoImage
              src={optimizeImageUrl(
                {
                  url: currentSelectedCategory.mobileLogoImageUrl,
                },
                isSSR,
                {
                  width: 312,
                  height: 158,
                }
              )}
              width={312}
              height={158}
            />
          </>
        ) : (
          <>
            <Spacer size="6x" />
            <LogoImage
              src={optimizeImageUrl(
                {
                  url: currentSelectedCategory.logoImageUrl,
                },
                isSSR,
                {
                  height: 100,
                  width: imageWidth,
                }
              )}
              height={100}
              width={imageWidth}
            />
          </>
        )}
      </Column>
      <Spacer size="3x" />
    </Column>
  );
};

const StaticCategories = observer(StaticCategoriesBase);

export const BrandsLandingPageTestimonials = observer(
  BrandsLandingPageTestimonialsBase
);

const Container = styled(Column)<{
  $bgColor: string;
  $opacity?: number;
  $showCaseStudies?: boolean;
}>`
  background-color: ${(props) =>
      props.$showCaseStudies ? Decorative.tint.neutral : props.$bgColor}${(
      props
    ) => props.$opacity};

  width: 100%;
  height: fit-content;
  ${(props) =>
    props.$showCaseStudies
      ? null
      : css`
          border: 24px solid white;
        `}
  ${responsiveMediaQueries.mobile} {
    border: 0;
    background-color: ${(props) => props.$bgColor}${(props) => props.$opacity};
  }
  transition: background-color 0.5s ease-in-out;
`;

const LogoImage = styled.img`
  object-position: center;
`;

const RoundedTitleWrapper = styled(Row)`
  overflow: auto;
  padding: 0 8px;
  ${responsiveMediaQueries.tabletAndBelow} {
    width: 100%;
    padding: 0 24px;
  }
`;

const RoundedTitle = styled(Typography)<{
  $isSelected: boolean;
}>`
  &&& {
    padding: 10px 18px;
    white-space: nowrap;
    background-color: ${(props) =>
      props.$isSelected ? Core.surface.default : ""};
    color: ${(props) => (props.$isSelected ? Core.text.inverse : "")};

    transition: background-color 0.2s ease-in-out;

    border-radius: 50px;
    &:hover {
      cursor: pointer;
    }
    margin-bottom: 24px;

    ${responsiveMediaQueries.mobile} {
      width: 100%;
      margin-bottom: 16px;
      padding: 10px 12px;
    }
  }
`;
